import { useEffect, useState } from "react";
import { useParams, Link, useNavigate, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Languages } from "lucide-react";
import { Monogram } from "@/components/Monogram";
import { Reveal } from "@/components/Reveal";
import { Lightbox } from "@/components/Lightbox";
import { useLang } from "@/lib/i18n";
import { PROJECTS, INDIA_PROJECTS, ALL_PROJECTS } from "@/lib/projects";

export default function ProjectDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { copy, lang, toggle } = useLang();
  const [lightbox, setLightbox] = useState(null);
  const globalIdx = ALL_PROJECTS.findIndex((p) => p.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (globalIdx === -1) return <Navigate to="/" replace />;

  const collection = ALL_PROJECTS[globalIdx].collection;
  const dataList = collection === "india" ? INDIA_PROJECTS : PROJECTS;
  const textList = collection === "india" ? copy.portfolio.indiaProjects : copy.portfolio.projects;
  const idx = dataList.findIndex((p) => p.slug === slug);
  const meta = textList[idx];
  const imgs = dataList[idx].gallery;
  const nextIdx = (idx + 1) % dataList.length;
  const next = { ...textList[nextIdx], ...dataList[nextIdx] };

  const goHome = (target) => {
    sessionStorage.setItem("alvirah_scroll", target);
    navigate("/");
  };

  return (
    <main data-testid="project-detail-page" className="bg-cream text-plum min-h-screen">
      <header className="fixed top-0 left-0 right-0 z-50 bg-cream/80 backdrop-blur-xl border-b hairline">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
          <button onClick={() => goHome("#portfolio")} data-testid="detail-back-btn" className="inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-plum/70 hover:text-plum transition-colors duration-300">
            <ArrowLeft size={16} strokeWidth={1.25} className="rtl:rotate-180" />
            {copy.portfolio.backToWorks}
          </button>
          <div className="flex items-center gap-4">
            <button
              data-testid="detail-lang-toggle"
              onClick={toggle}
              className="inline-flex items-center gap-2 border border-plum/25 text-plum text-[11px] uppercase tracking-[0.2em] px-3.5 py-2.5 hover:border-gold-dark hover:text-gold-dark transition-colors duration-300"
            >
              <Languages size={14} strokeWidth={1.25} />
              {lang === "en" ? "عربي" : "EN"}
            </button>
            <Link to="/" data-testid="detail-logo" className="text-plum">
              <Monogram className="h-8 w-auto" />
            </Link>
          </div>
        </div>
      </header>

      <section className="relative h-[72vh] min-h-[480px] overflow-hidden">
        <motion.img
          initial={{ scale: 1.12 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.8, ease: [0.16, 1, 0.3, 1] }}
          src={imgs[0]}
          alt={meta.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-plum/80 via-plum/20 to-plum/30" />
        <div className="absolute bottom-0 left-0 right-0 px-6 md:px-12 pb-12 md:pb-16 max-w-[1600px] mx-auto">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="text-[11px] uppercase tracking-[0.4em] text-cream/90 mb-4 hero-shadow"
          >
            {meta.location}
          </motion.p>
          <span className="block overflow-hidden">
            <motion.h1
              data-testid="detail-title"
              key={`t-${lang}`}
              initial={{ y: "110%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 1.2, delay: 0.45, ease: [0.16, 1, 0.3, 1] }}
              className="font-serif text-cream text-5xl sm:text-6xl lg:text-8xl tracking-tight leading-[0.95]"
            >
              {meta.name}
            </motion.h1>
          </span>
        </div>
      </section>

      <section className="px-6 md:px-12 max-w-[1600px] mx-auto py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-4">
            <div className="border-t hairline pt-6 flex flex-col gap-5">
              <div className="flex items-baseline justify-between gap-6">
                <span className="text-[10px] uppercase tracking-[0.3em] text-plum/45">{copy.portfolio.styleLabel}</span>
                <span className="font-serif text-lg">{meta.style}</span>
              </div>
              <div className="flex items-baseline justify-between gap-6">
                <span className="text-[10px] uppercase tracking-[0.3em] text-plum/45">{copy.portfolio.scopeLabel}</span>
                <span className="text-sm font-light text-plum/75 text-end">{meta.scope}</span>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-8">
            <p data-testid="detail-description" className="font-serif text-2xl md:text-4xl leading-snug tracking-tight text-plum/90">
              {meta.description}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="px-6 md:px-12 max-w-[1600px] mx-auto pb-16 md:pb-20">
        <Reveal>
          <p className="overline-label mb-10">{copy.portfolio.galleryOverline}</p>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8">
          {imgs.map((src, i) => (
            <Reveal key={src} delay={0.03} className={i === 0 ? "sm:col-span-2" : ""}>
              <figure
                data-testid={`detail-gallery-${i + 1}`}
                className="cursor-pointer"
                onClick={() => setLightbox(i)}
              >
                <div className="group overflow-hidden relative">
                  <img
                    src={src}
                    alt={meta.captions[i] || meta.name}
                    loading="lazy"
                    className={`w-full object-cover scale-100 group-hover:scale-105 transition-transform duration-[1.4s] ease-out ${i === 0 ? "h-[46vh] md:h-[64vh]" : "h-[36vh] md:h-[48vh]"}`}
                  />
                  <div className="absolute inset-0 bg-plum/0 group-hover:bg-plum/15 transition-colors duration-500" />
                </div>
                <figcaption className="mt-4 flex items-baseline gap-4">
                  <span className="text-[10px] tracking-[0.3em] text-gold-dark">{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-serif italic text-lg text-plum/70">{meta.captions[i] || ""}</span>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t hairline">
        <button
          data-testid="detail-next-project"
          onClick={() => navigate(`/portfolio/${next.slug}`)}
          className="w-full px-6 md:px-12 py-12 md:py-16 max-w-[1600px] mx-auto flex items-center justify-between gap-8 group text-start"
        >
          <div>
            <p className="overline-label mb-4">{copy.portfolio.nextProject}</p>
            <p className="font-serif text-4xl sm:text-5xl lg:text-7xl tracking-tight group-hover:text-gold-dark transition-colors duration-500">
              {next.name}
            </p>
          </div>
          <ArrowRight size={40} strokeWidth={1} className="text-gold-dark group-hover:translate-x-3 rtl:-scale-x-100 rtl:group-hover:-translate-x-3 transition-transform duration-500 shrink-0" />
        </button>
      </section>

      <section className="bg-plum text-cream px-6 md:px-12 py-14 md:py-20 text-center">
        <p className="text-[11px] uppercase tracking-[0.35em] text-cream/60 mb-6">{copy.quiz.overline}</p>
        <p className="font-serif text-3xl md:text-5xl tracking-tight max-w-2xl mx-auto leading-tight">
          {copy.portfolio.titleA} <span className="italic text-cream">{copy.portfolio.titleItalic}</span> {copy.portfolio.titleB}
        </p>
        <button
          data-testid="detail-cta-btn"
          onClick={() => goHome("#quiz")}
          className="mt-10 bg-gold text-cream text-[11px] uppercase tracking-[0.25em] px-10 py-4 hover:bg-cream hover:text-plum transition-colors duration-300"
        >
          {copy.portfolio.cta}
        </button>
      </section>

      <AnimatePresence>
        {lightbox !== null && (
          <Lightbox
            images={imgs}
            captions={meta.captions}
            index={lightbox}
            onClose={() => setLightbox(null)}
            onNavigate={setLightbox}
          />
        )}
      </AnimatePresence>
    </main>
  );
}
